import { ChevronDown } from "@/components/icons"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { useRegisterAdminDirty } from "@/features/admin-dashboard/admin-unsaved-changes-context"
import { cn } from "@/lib/utils"

import { useCollapsibleSection } from "../hooks/use-collapsible-section"

type CollapsibleManagementSectionProps = {
  children: React.ReactNode
  defaultExpanded?: boolean
  headerActions?: React.ReactNode
  icon: React.ReactNode
  id: string
  isDirty?: boolean
  storageKey: string
  title: string
}

export function CollapsibleManagementSection({
  children,
  defaultExpanded = true,
  headerActions,
  icon,
  id,
  isDirty,
  storageKey,
  title,
}: CollapsibleManagementSectionProps) {
  const { isOpen, setIsOpen } = useCollapsibleSection(storageKey, defaultExpanded)
  useRegisterAdminDirty(id, Boolean(isDirty))

  return (
    <Collapsible asChild open={isOpen} onOpenChange={setIsOpen}>
      <Card
        className="min-w-0 scroll-mt-24 self-start rounded-lg shadow-sm"
        data-admin-dirty={isDirty ? "true" : undefined}
        id={id}
      >
        <CardHeader className="flex flex-col gap-3 pb-4 sm:flex-row sm:items-center sm:justify-between">
          <CollapsibleTrigger asChild>
            <button
              className="focus-visible:ring-ring flex min-h-11 min-w-0 flex-1 items-center justify-between gap-3 rounded-md text-left focus-visible:ring-2 focus-visible:outline-none"
              type="button"
            >
              <CardTitle className="flex min-w-0 items-center gap-2 text-base">
                <span
                  aria-hidden="true"
                  className="text-muted-foreground flex size-5 shrink-0 items-center justify-center"
                >
                  {icon}
                </span>
                <span className="truncate">{title}</span>
              </CardTitle>
              <ChevronDown
                aria-hidden="true"
                className={cn("size-4 shrink-0 transition-transform", isOpen && "rotate-180")}
              />
            </button>
          </CollapsibleTrigger>
          {headerActions ? (
            <div className="ml-auto flex min-h-11 min-w-0 flex-wrap items-center gap-2">
              {headerActions}
            </div>
          ) : null}
        </CardHeader>
        <CollapsibleContent forceMount className="data-[state=closed]:hidden" hidden={!isOpen}>
          <CardContent className="min-w-0 pt-0">{children}</CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  )
}
